/**
 * docValidityModel — the data behind every "Document validity" surface on the
 * Documents tab (the insight panel's reference columns and the Document
 * Validity sub-tab).
 *
 * It does NOT own any rule. The anchor day, the closing projection and the
 * windows all come from the shared helpers (see "Document validity &
 * expiration" in CLAUDE.md):
 *   - getValidityReference / getClosingProjection  (utils/documents/validityReference)
 *   - resolveValidityDays                           (LoanExpiringDocumentsRenderer)
 * This file only shapes them into reference days + per-document rows.
 *
 * All day math is on LOCAL-midnight epoch ms built from calendar fields, so a
 * `YYYY-MM-DD` never slips a day in a negative-offset zone.
 */
import { computeExpirationDate, daysUntilDate } from "../../../../constants/documents/docTypeExpirationDurations";
import {
  VALIDITY_DATE_FIELD_LABEL,
  anchorFieldsFor,
  getClosingProjection,
  getValidityReference,
  projectionFieldsFor,
} from "../../../../utils/documents/validityReference";
import {
  EXPIRING_SOON_THRESHOLD_DAYS,
  resolveValidityDays,
} from "../LoanDrilldownFilesTab/LoanExpiringDocumentsRenderer";

export const STATUSES = ["valid", "expiring", "expired"];
export const STATUS_LABEL = {
  valid: "Valid",
  expiring: "Expiring soon",
  expired: "Expired",
};

const DAY = 86400000;

/** Local-midnight ms of a date-only value (or of today with no argument). */
export const dayMsOf = (value) => {
  if (value == null) {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), n.getDate()).getTime();
  }
  const s = String(value).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const d = new Date(value);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  }
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d).getTime();
};

/** Local-midnight ms → "YYYY-MM-DD" (feed to formatDate). */
export const dayKey = (dayMs) => {
  const d = new Date(dayMs);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
};

export const diffDays = (aMs, bMs) => Math.round((aMs - bMs) / DAY);

// "YEAR_END" windows (Flood Certificate in the legacy map) end Dec 31 of the
// effective year; everything else is effective + N days.
const expirationYmd = (effective, days) => {
  if (days === "YEAR_END") return `${String(effective).slice(0, 4)}-12-31`;
  return computeExpirationDate(effective, days);
};

const statusFor = (daysLeft) =>
  daysLeft < 0
    ? "expired"
    : daysLeft <= EXPIRING_SOON_THRESHOLD_DAYS
      ? "expiring"
      : "valid";

/**
 * The reference days this loan's documents are judged on.
 *
 *   live, closing on file   today (sets status) + anchor (upcoming closing)
 *   live, no future closing today only — `slipped` lists past dates on file
 *   closed, anchor resolved anchor (frozen, sets status) + today (reference)
 *   closed, nothing on file today only, `unresolved` + the fields to fill in
 *
 * `statusKey` is the ref whose status the rest of the tab reports.
 */
export const buildReferences = (loan, { isAcquisition = false } = {}) => {
  const opts = { isAcquisition };
  const today = dayMsOf();
  const reference = getValidityReference(loan, opts);
  const todayRef = { key: "today", dayMs: today, name: "Today", field: null };

  if (reference.frozen) {
    return {
      closed: true,
      today,
      statusKey: "anchor",
      refs: [
        {
          key: "anchor",
          dayMs: dayMsOf(reference.closingDate),
          name: "At close",
          role: "frozen",
          field: reference.closingField,
          fieldLabel: VALIDITY_DATE_FIELD_LABEL[reference.closingField] || reference.closingField,
        },
        { ...todayRef, role: "reference" },
      ],
    };
  }
  if (reference.unresolved) {
    return {
      closed: true,
      unresolved: true,
      today,
      statusKey: "today",
      missingFields: anchorFieldsFor(opts).map((f) => VALIDITY_DATE_FIELD_LABEL[f] || f),
      refs: [{ ...todayRef, role: "sets" }],
    };
  }

  const projection = getClosingProjection(loan, opts);
  if (!projection) {
    const slipped = projectionFieldsFor(opts)
      .filter((f) => loan?.[f])
      .map((f) => ({ field: f, label: VALIDITY_DATE_FIELD_LABEL[f] || f, dayMs: dayMsOf(loan[f]) }))
      .filter((x) => x.dayMs < today);
    return {
      closed: false,
      today,
      statusKey: "today",
      slipped,
      refs: [{ ...todayRef, role: "sets" }],
    };
  }
  return {
    closed: false,
    today,
    statusKey: "today",
    refs: [
      { ...todayRef, role: "sets" },
      {
        key: "anchor",
        dayMs: projection.closingMs,
        name: "At closing",
        role: "upcoming",
        field: projection.closingField,
        fieldLabel: VALIDITY_DATE_FIELD_LABEL[projection.closingField] || projection.closingField,
      },
    ],
  };
};

/**
 * One row per dated document, judged on every reference day:
 *   { doc, effMs, expMs, days, daysLeft: {today, anchor}, at: {today, anchor}, lapse }
 * `lapse` = good today, expired by the upcoming closing (live loans only).
 * Also counts the documents with no window (`undated`) and the ones that have
 * a window but no start date (`missingStart`, with their ids).
 */
export const buildRows = (documents, refInfo) => {
  const rows = [];
  let undated = 0;
  const missingStartIds = [];
  (documents || []).forEach((doc) => {
    const days = resolveValidityDays(doc);
    if (days == null) {
      undated += 1;
      return;
    }
    if (!doc?.reportEffectiveDate) {
      missingStartIds.push(doc?._id);
      return;
    }
    const exp = expirationYmd(doc.reportEffectiveDate, days);
    if (!exp) {
      undated += 1;
      return;
    }
    const at = {};
    const daysLeft = {};
    refInfo.refs.forEach((r) => {
      daysLeft[r.key] = daysUntilDate(exp, r.dayMs) ?? 0;
      at[r.key] = statusFor(daysLeft[r.key]);
    });
    rows.push({
      doc,
      days,
      effMs: dayMsOf(doc.reportEffectiveDate),
      expMs: dayMsOf(exp),
      daysLeft,
      at,
      lapse: !refInfo.closed && !!at.anchor && at.today !== "expired" && at.anchor === "expired",
    });
  });
  rows.sort((a, b) => a.expMs - b.expMs);
  return { rows, undated, missingStartIds };
};

/** Everything a validity surface needs, in one call. */
export const buildValidityModel = ({ loan, documents, isAcquisition }) => {
  const refInfo = buildReferences(loan, { isAcquisition });
  return { ...refInfo, ...buildRows(documents, refInfo) };
};

export const countAt = (rows, refKey) => {
  const c = { valid: 0, expiring: 0, expired: 0 };
  rows.forEach((r) => {
    if (r.at[refKey]) c[r.at[refKey]] += 1;
  });
  return c;
};

/** Valid today → inside the 10-day window by closing (live loans). */
export const enteringWindow = (rows, closed) =>
  closed ? [] : rows.filter((r) => r.at.today === "valid" && r.at.anchor === "expiring");

/**
 * Filters are strings so the tab can hold them in one piece of state:
 *   "<refKey>:<status>"   status on that day (e.g. "today:expired", "anchor:valid")
 *   "anchor:lapse"        good today, expired by closing
 *   "missingStart"        has a window, no start date
 */
export const parseFilter = (filter) => {
  if (!filter) return null;
  if (filter === "missingStart") return { status: "missingStart" };
  const [refKey, status] = String(filter).split(":");
  return { refKey, status };
};

export const rowMatches = (row, filter) => {
  const f = parseFilter(filter);
  if (!f) return true;
  if (f.status === "lapse") return row.lapse;
  return row.at[f.refKey] === f.status;
};

/** Ids of the docs a filter selects (for the documents table). */
export const idsForFilter = (model, filter) => {
  const f = parseFilter(filter);
  if (!f) return null;
  if (f.status === "missingStart") return new Set(model.missingStartIds);
  return new Set(model.rows.filter((r) => rowMatches(r, filter)).map((r) => r.doc?._id));
};

/** "expired 15d ago" / "4d left" / "lapsed 23d before" — how a row reads on a day. */
export const daysText = (row, refKey) => {
  const d = row.daysLeft[refKey];
  if (refKey === "today") return d < 0 ? `expired ${-d}d ago` : d === 0 ? "expires today" : `${d}d left`;
  return d < 0 ? `lapsed ${-d}d before` : d === 0 ? "expires that day" : `${d}d left`;
};

/** A shared axis for the timeline: padded bounds + month-start ticks. */
export const buildScale = (rows, refs) => {
  const points = [...rows.flatMap((r) => [r.effMs, r.expMs]), ...refs.map((r) => r.dayMs)];
  if (!points.length) return null;
  const lo = Math.min(...points) - 6 * DAY;
  const hi = Math.max(...points) + 6 * DAY;
  const ticks = [];
  const d = new Date(lo);
  let t = new Date(d.getFullYear(), d.getMonth() + 1, 1).getTime();
  while (t < hi) {
    ticks.push(t);
    const x = new Date(t);
    t = new Date(x.getFullYear(), x.getMonth() + 1, 1).getTime();
  }
  const pct = (ms) => `${(((ms - lo) / (hi - lo)) * 100).toFixed(2)}%`;
  return { lo, hi, ticks, pct };
};
